import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { AddressForm, IconButton } from '../../../../compositions';
import { FormProps } from '../../../../entities/@forms/Form/Form';
import { AddressType, CustomerAddress, generateDefaultAddress } from '../../../../entities/Address/Address';
import { defaultCountry } from '../../../../entities/Country/Country';
import { useTrans } from '../../../../hooks';

import './MyAddresses.scss';

interface MyAddressesProps extends FormProps<CustomerAddress> {
    successfullyAddedAddress?: boolean;
    addresses: CustomerAddress[];
    onChangeShippingAddress: (addressId: string) => void;
    className?: string;
}

// TODO: Refactor address forms and behavior
// - Allow deleting addresses
// - Fix wonky 'Shipping/Billing' stars: 1 star per type for all addresses
export const MyAddresses: FC<MyAddressesProps> = ({
    successfullyAddedAddress,
    addresses,
    onSubmit,
    onChangeShippingAddress,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const [openNewAddress, setOpenNewAddress] = useState<boolean>(false);

    useEffect((): void => {
        if (successfullyAddedAddress) {
            setOpenNewAddress(false);
        }
    }, [successfullyAddedAddress]);

    const handleEditButton = (): void => {
        setOpenNewAddress(true);
    };

    const handleCloseNewAddress = (): void => {
        setOpenNewAddress(false);
    };

    const defaultAddress: CustomerAddress = {
        ...generateDefaultAddress(),
        type: AddressType.billing,
        label: trans(`entities.address.type.${AddressType.billing}`),
        country: defaultCountry,
    };

    const handleShippingAddressButtonClick = (addressId: string, type: AddressType): void => {
        const matchingAddress = addresses.find(item => item.id === addressId);

        if (!matchingAddress) {
            return;
        }

        const isBillingToShipping = matchingAddress.type === AddressType.billing && type === AddressType.shipping;

        if (isBillingToShipping) {
            onChangeShippingAddress(addressId);
            return;
        }

        const otherAddress = addresses.find(item => item.type === AddressType.billing);

        if (otherAddress) {
            onChangeShippingAddress(otherAddress.id);
        }
    };

    return (
        <div className={`my-addresses ${className}`}>
            <div className="my-addresses__header">
                <h2 className="my-addresses__heading">
                    {trans('containers.accountDashboardConfig.myAddresses.title')}
                </h2>

                {addresses.length < 2 && !openNewAddress && (
                    <IconButton
                        type="submit"
                        icon="plus"
                        text={trans('containers.accountDashboardConfig.myAddresses.addNewAddress')}
                        onClick={handleEditButton}
                        className="my-addresses__edit-button"
                    />
                )}
            </div>

            {addresses && (
                <div className="my-addresses__list">
                    {addresses.map(address => (
                        <AddressForm
                            key={address.id}
                            address={address}
                            onSubmit={onSubmit}
                            onChangeShippingAddress={handleShippingAddressButtonClick}
                        />
                    ))}

                    {openNewAddress && addresses.length < 2 && (
                        <AddressForm
                            isEditModeOn
                            address={defaultAddress}
                            onSubmit={onSubmit}
                            onChangeShippingAddress={handleShippingAddressButtonClick}
                            onCloseClick={handleCloseNewAddress}
                            className="my-addresses__new-address-form"
                        />
                    )}
                </div>
            )}
        </div>
    );
};
